// M1-3: URL parameter parsing. Will be expanded with preview token, anchors, etc.

import { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Vec3 } from '@mindcloudxai_3d/core';

export type MockTrackType = 'orbit' | 'flythrough' | 'figure8';
export type RendererType = 'three' | 'playcanvas';
export type DeviceType = 'webgl' | 'webgpu';

/**
 * Vision Pro（Safari + WebXR）不支持 WebGPU，默认回退 WebGL。
 * 其他设备保持 WebGPU 默认，VR 入口仍可显式切到 WebGL。
 * 注意：这是近似检测——Vision Pro Safari UA 无独立标识，
 * 当前仅靠 navigator.xr 存在 + Safari 排除法判断；
 * 若未来桌面 Safari 也支持 WebXR，此判断会误判桌面 Mac 为 Vision Pro。
 */
function isVisionProSafari(): boolean {
  if (typeof navigator === 'undefined' || !navigator.xr) return false;
  const ua = navigator.userAgent;
  // Safari 且非 Chrome/Edge（Chrome on Mac 也含 Safari 字样但含 Chrome）
  const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|Edg|FxiOS/.test(ua);
  return isSafari;
}

export interface ExploreUrlParams {
  sceneId: string | undefined;
  /** ?url= 直链（跳过 sceneId 路径直接加载 splat） */
  directUrl: string | null;
  taskCode: string | null;
  taskId: number | null;
  previewToken: string | null;
  hotspotId: string | null;
  /** 调试用：切换底层渲染器，默认 playcanvas */
  renderer: RendererType;
  /** PlayCanvas 底层图形 API，默认 WebGPU；VR 入口会显式切到 WebGL。 */
  deviceType: DeviceType;
  /** 碰撞数据 URL：Three.js 传 GLB，PlayCanvas 传 voxel JSON；各 adapter 在 loadSplat 内部自行处理。 */
  collisionUrl: string | null;
  /** 开启碰撞体可视化 wireframe */
  debugCollision: boolean;
  debugTrackRoute: boolean;
  mockTrack: MockTrackType | null;
  trackDuration: number;
  trackCenter: Vec3 | null;
  /** 自定义 avatar GLB URL；未指定时使用内置默认 character.glb */
  avatarUrl: string | null;
  /**
   * 运行时性能浮层（MiniStats）。需 ?ministats 显式开启，默认关闭。
   * 注意：MiniStats 是 2D DOM 覆盖层，仅在普通页面可见，进入 VR 沉浸式后不可见。
   */
  ministats: boolean;
}

function parseVec3(raw: string | null): Vec3 | null {
  if (!raw) return null;
  const parts = raw.split(',').map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
  return new Vec3(parts[0], parts[1], parts[2]);
}

function parsePositiveInt(raw: string | null): number | null {
  if (!raw || !/^\d+$/.test(raw)) return null;
  const value = Number(raw);
  return Number.isSafeInteger(value) && value > 0 ? value : null;
}

const VALID_MOCK_TRACKS = new Set<MockTrackType>(['orbit', 'flythrough', 'figure8']);

export function useUrlParams(): ExploreUrlParams {
  const { sceneId: routeSceneId } = useParams<{ sceneId: string }>();
  const [search] = useSearchParams();

  return useMemo(() => {
    const mockRaw = search.get('mockTrack') as MockTrackType | null;
    const rendererRaw = search.get('renderer');
    const deviceTypeRaw = search.get('deviceType');
    const legacyTaskId = search.get('taskId');
    const taskId = parsePositiveInt(
      search.get('task_id') ?? search.get('target_id') ?? legacyTaskId,
    );
    const taskCode = routeSceneId ?? search.get('task_code') ?? (taskId ? null : legacyTaskId);
    const sceneId = taskCode ?? (taskId ? String(taskId) : undefined);
    return {
      sceneId,
      directUrl: search.get('url'),
      taskCode,
      taskId,
      previewToken: search.get('preview') ?? search.get('token'),
      hotspotId: search.get('hotspot'),
      renderer: rendererRaw === 'three' ? 'three' : 'playcanvas',
      deviceType:
        deviceTypeRaw === 'webgl' || search.has('webgl') || isVisionProSafari()
          ? 'webgl'
          : 'webgpu',
      collisionUrl: search.get('collisionUrl') ?? search.get('meshUrl'),
      debugCollision: search.get('debugCollision') === '1',
      debugTrackRoute: search.get('debugTrackRoute') === '1',
      mockTrack: mockRaw && VALID_MOCK_TRACKS.has(mockRaw) ? mockRaw : null,
      trackDuration: Number(search.get('trackDuration')) || 10,
      trackCenter: parseVec3(search.get('trackCenter')),
      avatarUrl: search.get('avatar') ?? search.get('avatarUrl'),
      ministats: search.has('ministats'),
    };
  }, [routeSceneId, search]);
}
